import React, { useCallback, useMemo } from "react";
import {
  getLoginDialogTitleAndDescription,
  getShouldShow,
} from "scmp-sdk/components/acquisition-popup-container/display/helpers";
import type {
  PopRenderPayload,
  PopupGetter,
  useRenderPopupOptions,
} from "scmp-sdk/components/acquisition-popup-container/display/types";
import { useSdkContext } from "scmp-sdk/components/contexts";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { TopicPackageFollowPopup } from ".";
import { parseConfigFromQueryAction } from "./actions";
import { SanitizedUrlKeys } from "./consts";

export function useTopicPackageFollowPopupGetter(options: useRenderPopupOptions): PopupGetter {
  const {
    acquisition: { popupState },
    currentRoute,
    handleOpenLoginDialog,
    handleRemoveRouteQuery: handleRemoveRouteQuery_,
    userCountry,
    userTypeIds,
  } = useSdkContext();

  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);

  const handleRemoveRouteQuery = useCallback(
    () => handleRemoveRouteQuery_(SanitizedUrlKeys),
    [handleRemoveRouteQuery_],
  );

  const getNextFromAction = useCallback(
    (handleClose: () => void): PopRenderPayload | undefined => {
      const parsedConfig = parseConfigFromQueryAction(
        new URL(currentRoute, globalThis.location.origin).search,
      );
      if (!parsedConfig) return;

      const matchedConfig = popupState.topicPackageFollows[parsedConfig.configKey];
      if (!matchedConfig) return;

      if (!options.isLoggedIn) {
        const { description, title } = getLoginDialogTitleAndDescription({
          campaignName: matchedConfig.displayName,
          type: "topics",
        });

        handleOpenLoginDialog({
          description,
          destination: globalThis.location.href,
          ga4CustomParameter: {
            internal_campaign: matchedConfig?.trackingActionName,
            trigger_point: "ppto",
          },
          title,
        });
        void handleRemoveRouteQuery();
        return;
      }

      const props = {
        confirmedConfig: {
          checkedItemIndex: parsedConfig.checkedIndex,
        },
        modalContainerReference: options.modalContainerReference,
        onClose: handleClose,
        topicPackageFollow: { ...matchedConfig, key: parsedConfig.configKey },
      };

      void handleRemoveRouteQuery();

      return {
        element: <TopicPackageFollowPopup {...props} />,
        localStorageKey: matchedConfig.localStorageKey,
      };
    },
    [
      currentRoute,
      handleOpenLoginDialog,
      handleRemoveRouteQuery,
      options.isLoggedIn,
      options.modalContainerReference,
      popupState.topicPackageFollows,
    ],
  );

  const getNextFromConfig = useCallback(
    (handleClose: () => void): PopRenderPayload | undefined => {
      const nextConfig = Object.entries(popupState.topicPackageFollows).find(([_, config]) =>
        getShouldShow({
          config,
          userInfo: {
            isLoggedIn: options.isLoggedIn,
            userCountry: userCountry ?? "",
            userDevice: isMobile ? "mobile" : "desktop",
            userTypeIds,
          },
        }),
      );

      if (!nextConfig) return;

      const [key, config] = nextConfig;

      const props = {
        modalContainerReference: options.modalContainerReference,
        onClose: handleClose,
        topicPackageFollow: { ...config, key },
        trackingName: config.trackingActionName,
      };

      return {
        delay: Number.parseInt(config.timeout, 10),
        element: <TopicPackageFollowPopup {...props} />,
        localStorageKey: config.localStorageKey,
      };
    },
    [
      isMobile,
      options.isLoggedIn,
      options.modalContainerReference,
      popupState.topicPackageFollows,
      userCountry,
      userTypeIds,
    ],
  );

  const getters = useMemo(
    () => ({ getNextFromAction, getNextFromConfig }),
    [getNextFromConfig, getNextFromAction],
  );

  return getters;
}
